/**
 * Admin equipment list.
 *
 * Behaviour: on-blur autosave of name / rate / qty, an "Add Item" modal, and
 * per-row delete behind a confirmation modal. Everything else about an item —
 * roles, physical facts, sourcing — is edited on /equipment/:id, which each row
 * links to (#333).
 *
 * The full CRUD surface (GET/POST/PATCH/DELETE) lives in the Go service — see
 * go-service/internal/api/equipment.go (#108).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AdminPageHeader } from "../components/ui/AdminPageHeader";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { Tooltip } from "../components/ui/Tooltip";
import { ChevronRightIcon, PlusIcon, TrashIcon } from "../components/ui/icons";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/ResponsiveTable";
import { ApiError } from "../../../shared/api-client";
import {
  listEquipment,
  patchEquipment,
  deleteEquipment,
  listGearRoles,
  roleId,
  type EquipmentField,
  type EquipmentItem,
  type EquipmentPatch,
  type GearRole,
} from "../lib/equipment-api";
import { listWarehouses, type CompanyWarehouse } from "../lib/warehouses-api";
import { EquipmentCreateModal } from "../components/admin/EquipmentCreateModal";
import { Select } from "../components/ui/Select";
import {
  nameLengthRule,
  qtyFormatRule,
  rateRule,
} from "../components/admin/validators";
import { useEditableField } from "../lib/use-editable-field";
import { checkField } from "@shared/validators";
import { InlineBanner } from "../components/ui/InlineBanner";
import { cx } from "@shared/cx";
import a11y from "@shared/a11y.module.css";
import page from "./page.module.css";
import styles from "./equipment.module.css";

export default function EquipmentPage() {
  const navigate = useNavigate();
  // The warehouse filter is a place, so it lives in the URL (DESIGN-SYSTEM.md, Admin
  // list pages) and is committed with `replace` — tweaking a filter must not
  // fill the Back history. Search stays local state; making it URL-backed is a
  // separate change with its own debouncing question.
  const [searchParams, setSearchParams] = useSearchParams();
  const warehouseFilter = searchParams.get("warehouse") ?? "";
  const [warehouses, setWarehouses] = useState<CompanyWarehouse[]>([]);
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Every in-flight field save keyed "<itemId>:<field>". The cell currently
  // saving stays interactive; every other cell dims via disabled:opacity-60.
  const [savingFields, setSavingFields] = useState<Set<string>>(
    () => new Set(),
  );
  const [error, setError] = useState<string | null>(null);
  const [deleteItem, setDeleteItem] = useState<EquipmentItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Delete failures render inside the confirm modal (next to the item being
  // deleted), not in the page-level banner — the row that caused the error is
  // usually nowhere near the top of the page.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Gear-role ladders drive the list's role sections (and the role search).
  // Role create/rename/delete live in the detail page's role picker (#320),
  // not here — this list only groups by them.
  const [gearRoles, setGearRoles] = useState<GearRole[]>([]);

  useEffect(() => {
    document.title = "Equipment — Rental Admin";
  }, []);

  const busy = savingFields.size > 0 || deleting;
  const isSaving = (itemId: string, field: EquipmentField): boolean =>
    savingFields.has(`${itemId}:${field}`);

  const fetchItems = async (signal?: AbortSignal): Promise<void> => {
    try {
      const parsed = await listEquipment(signal, warehouseFilter || undefined);
      if (signal?.aborted) return;
      setItems(parsed);
      setError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // Gear-role ladders group the list into role sections. Best-effort — a
  // failure here doesn't block the equipment list (every item falls into
  // "Unassigned").
  const fetchRefData = async (signal?: AbortSignal): Promise<void> => {
    try {
      const roles = await listGearRoles(signal);
      if (signal?.aborted) return;
      setGearRoles(roles);
    } catch {
      // non-fatal
    }
    try {
      // Warehouses (#304). One active site draws no filter at all. Best-effort
      // like the roles above: a Company that cannot read its warehouses still
      // gets its catalogue.
      const rows = await listWarehouses(signal);
      if (signal?.aborted) return;
      setWarehouses(rows);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    void fetchItems(ac.signal);
    void fetchRefData(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseFilter]);

  // The one gate for every per-warehouse control on this page: above one
  // active site, stock is a split and the list can only report its sum.
  const activeWarehouses = useMemo(
    () => warehouses.filter((w) => w.active),
    [warehouses],
  );
  const splitStock = activeWarehouses.length > 1;

  const saveField = async (
    item: EquipmentItem,
    field: EquipmentField,
    patch: EquipmentPatch,
  ): Promise<void> => {
    const key = `${item.id}:${field}`;
    setSavingFields((prev) => new Set(prev).add(key));
    // Optimistic update — reconciled by a refetch if the save fails.
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, ...patch } : i)),
    );
    try {
      await patchEquipment(item.id, patch);
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : String(err),
      );
      await fetchItems();
    } finally {
      setSavingFields((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await deleteEquipment(deleteItem.id);
      setDeleteItem(null);
      setDeleteError(null);
      await fetchItems();
    } catch (err) {
      // Keep the modal open so the error shows beside the item it belongs to.
      setDeleteError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : String(err),
      );
    } finally {
      setDeleting(false);
    }
  };

  const roleNamesByEquipmentId = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const role of gearRoles) {
      for (const member of role.members) {
        const names = m.get(member.equipment_id) ?? [];
        names.push(role.display_name);
        m.set(member.equipment_id, names);
      }
    }
    return m;
  }, [gearRoles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const roleNames = roleNamesByEquipmentId.get(item.id) ?? [];
      return (
        item.name.toLowerCase().includes(q) ||
        roleNames.some((roleName) => roleName.toLowerCase().includes(q))
      );
    });
  }, [items, roleNamesByEquipmentId, search]);

  // Section headers carry the profile label only when the company runs more
  // than one enabled profile — single-profile tenants render as before.
  const multiProfile = useMemo(
    () => new Set(gearRoles.map((r) => r.profile_key)).size > 1,
    [gearRoles],
  );

  const roleSections = useMemo(() => {
    const itemById = new Map(items.map((item) => [item.id, item]));
    const filteredIds = new Set(filtered.map((item) => item.id));
    const assignedIds = new Set<string>();
    const sections: {
      key: string;
      role: GearRole | null;
      items: EquipmentItem[];
    }[] = [];

    for (const role of gearRoles) {
      const sectionItems: EquipmentItem[] = [];
      for (const member of role.members) {
        assignedIds.add(member.equipment_id);
        const item = itemById.get(member.equipment_id);
        if (item && filteredIds.has(item.id)) {
          sectionItems.push(item);
        }
      }
      if (sectionItems.length > 0) {
        sections.push({ key: roleId(role), role, items: sectionItems });
      }
    }

    const unassigned = filtered.filter((item) => !assignedIds.has(item.id));
    if (unassigned.length > 0) {
      sections.push({ key: "__unassigned", role: null, items: unassigned });
    }
    return sections;
  }, [filtered, gearRoles, items]);

  const renderSection = (section: {
    key: string;
    role: GearRole | null;
    items: EquipmentItem[];
  }) => (
    <div key={section.key}>
      <h2 className={styles.roleHeading}>
        <span>{section.role ? section.role.display_name : "Unassigned"}</span>
        {section.role && multiProfile && (
          <span className={styles.roleProfile}>
            {" "}
            · {section.role.profile_label}
          </span>
        )}
      </h2>
      <ResponsiveTable>
        <TableHead>
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell width="28">Rate</TableHeaderCell>
          <TableHeaderCell width="20">Qty</TableHeaderCell>
          <TableHeaderCell width="24">
            <span className={a11y.srOnly}>Actions</span>
          </TableHeaderCell>
        </TableHead>
        <TableBody>
          {section.items.map((item) => (
            <EquipmentRow
              key={item.id}
              item={item}
              busy={busy}
              splitStock={splitStock}
              isSaving={isSaving}
              onSaveField={saveField}
              onDelete={() => {
                setDeleteError(null);
                setDeleteItem(item);
              }}
            />
          ))}
        </TableBody>
      </ResponsiveTable>
    </div>
  );

  return (
    <div className={page.page}>
      <div className={page.content}>
        <AdminPageHeader
          title="Equipment"
          subtitle={`${items.length} items${gearRoles.length > 0 ? ` · ${gearRoles.length} roles` : ""}`}
        />

        {error && (
          <Banner
            variant="error"
            className={styles.banner}
            onDismiss={() => setError(null)}
          >
            {error}
          </Banner>
        )}

        <EquipmentCreateModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(newId, stayOpen) => {
            void fetchItems();
            void fetchRefData();
            // Not staying open means the operator is done typing — take them
            // to the item so roles/sourcing are one step away, not a hunt.
            if (!stayOpen && newId) void navigate(`/equipment/${newId}`);
          }}
        />

        <div className={styles.toolbar}>
          {/* The filter row wraps rather than squeezing: the search field has a
              floor, so a narrow window drops the warehouse Select onto its own
              line instead of truncating the placeholder. */}
          <div className={styles.filterRow}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search equipment or roles..."
              className={styles.search}
            />
            {/* One warehouse, no filter: a single-site Company sees this page
                exactly as before. */}
            {splitStock && (
              <Select
                size="sm"
                ariaLabel="Warehouse"
                className={styles.warehouseSelect}
                value={warehouseFilter}
                options={[
                  { value: "", label: "All warehouses" },
                  ...activeWarehouses.map((w) => ({
                    value: w.id,
                    label: w.name,
                  })),
                ]}
                onChange={(next) => {
                  const params = new URLSearchParams(searchParams);
                  if (next === "") params.delete("warehouse");
                  else params.set("warehouse", next);
                  setSearchParams(params, { replace: true });
                }}
              />
            )}
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} disabled={busy}>
            <span className={styles.addLabel}>
              <PlusIcon className={styles.icon} />
              Add Item
            </span>
          </Button>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading equipment…</div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>No equipment to show.</div>
        ) : (
          <div className={styles.sections}>
            {roleSections.map((section) => renderSection(section))}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!deleteItem}
        title="Delete equipment"
        message={
          <>
            Are you sure you want to delete <strong>{deleteItem?.name}</strong>?
          </>
        }
        error={deleteError}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteItem(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

interface EquipmentRowProps {
  item: EquipmentItem;
  busy: boolean;
  /** More than one active Warehouse: the quantity is a sum, not a field. */
  splitStock: boolean;
  isSaving: (itemId: string, field: EquipmentField) => boolean;
  onSaveField: (
    item: EquipmentItem,
    field: EquipmentField,
    patch: EquipmentPatch,
  ) => void;
  onDelete: () => void;
}

function EquipmentRow({
  item,
  busy,
  splitStock,
  isSaving,
  onSaveField,
  onDelete,
}: EquipmentRowProps) {
  const name = useEditableField(item.name, {
    validate: (v) => checkField(v, nameLengthRule),
    onSave: (v) => {
      if (v !== null) onSaveField(item, "name", { name: v });
    },
  });

  const rate = useEditableField(String(item.hire_rate_per_day), {
    validate: (v) => checkField(v, rateRule),
    onSave: (v) => {
      const n = v === null ? 0 : Number(v);
      if (n !== item.hire_rate_per_day) {
        onSaveField(item, "hire_rate_per_day", { hire_rate_per_day: n });
      }
    },
  });

  const qty = useEditableField(String(item.stock_quantity), {
    validate: (v) => checkField(v, qtyFormatRule),
    onSave: (v) => {
      const n = v === null ? 0 : Number(v);
      if (n !== item.stock_quantity) {
        onSaveField(item, "stock_quantity", { stock_quantity: n });
      }
    },
  });

  const cls = (fieldError: string | undefined): string =>
    cx(styles.cell, fieldError && styles.cellInvalid);

  return (
    <TableRow columns={4}>
      <TableCell
        label="Name"
        span={4}
        // cornerGap reserves room for the corner open/delete controls, which
        // are absolutely positioned over the card's top-right below the
        // breakpoint.
        padding="default"
        cornerGap="lg"
      >
        <input
          type="text"
          value={name.draft}
          onChange={(e) => name.setDraft(e.target.value)}
          onFocus={name.onFocus}
          onBlur={name.onBlur}
          aria-label="Name"
          aria-invalid={Boolean(name.error) || undefined}
          disabled={busy && !isSaving(item.id, "name")}
          className={cls(name.error)}
        />
        {/* The "Hire-in" badge sits BELOW the name (not inline) so it never
            steals horizontal room and shove the name off a narrow column. */}
        {item.is_hired_in && (
          <span className={styles.hireInBadge}>
            <Tooltip content="Sourced from a Supplier, not counted as owned stock.">
              Hire-in
            </Tooltip>
          </span>
        )}
        {name.error && (
          <InlineBanner variant="error">{name.error}</InlineBanner>
        )}
      </TableCell>
      <TableCell label="Rate" span={2}>
        <div className={styles.rateRow}>
          <span className={styles.currency}>$</span>
          <input
            type="text"
            inputMode="decimal"
            value={rate.draft}
            onChange={(e) => rate.setDraft(e.target.value)}
            onFocus={rate.onFocus}
            onBlur={rate.onBlur}
            aria-label="Hire rate per day"
            aria-invalid={Boolean(rate.error) || undefined}
            disabled={busy && !isSaving(item.id, "hire_rate_per_day")}
            className={cls(rate.error)}
          />
        </div>
        {rate.error && (
          <InlineBanner variant="error">{rate.error}</InlineBanner>
        )}
      </TableCell>
      <TableCell label="Qty" span={2}>
        {splitStock ? (
          // The sum across warehouses — editing it here could only guess which
          // site the change belonged to, so the split is set on the item page.
          <span
            className={cx(
              styles.qtySum,
              item.stock_quantity === 0 && styles.zero,
            )}
          >
            <Tooltip content="Stock is split across warehouses — set it on the item's page.">
              {String(item.stock_quantity)}
            </Tooltip>
          </span>
        ) : (
          <>
            <input
              type="text"
              inputMode="numeric"
              value={qty.draft}
              onChange={(e) => qty.setDraft(e.target.value)}
              onFocus={qty.onFocus}
              onBlur={qty.onBlur}
              aria-label="Stock quantity"
              aria-invalid={Boolean(qty.error) || undefined}
              disabled={busy && !isSaving(item.id, "stock_quantity")}
              className={cx(
                cls(qty.error),
                item.stock_quantity === 0 && styles.zero,
              )}
            />
            {qty.error && (
              <InlineBanner variant="error">{qty.error}</InlineBanner>
            )}
          </>
        )}
      </TableCell>
      <TableCell corner className={styles.nowrap}>
        <div className={styles.rowActions}>
          {/* The aria-label IS each icon control's name — no tooltip repeats it. */}
          <Link
            to={`/equipment/${item.id}`}
            aria-label={`Open ${item.name}`}
            className={styles.iconControl}
          >
            <ChevronRightIcon className={styles.icon} />
          </Link>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            aria-label={`Delete ${item.name}`}
            className={styles.iconControl}
          >
            <TrashIcon className={styles.icon} />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}
